import { useMemo } from 'react'
import { Field } from 'formik'
import { useTranslation } from 'react-i18next'
import { CheckboxField, BoosterField } from '~/components'
import { getVehicleOptionPrice } from '~/utils/data'
import { VEHICLE_OPTIONS_CONFIG, processVehicleOption } from './vehicleOptionsConfig'
import AddOnsCard from './AddOnsCard'

/**
 * Renders vehicle options inside a collapsible AddOnsCard.
 * @param {object} props
 * @param {array}   props.vehicleOptions         - Available vehicle options from API
 * @param {array}   props.editableVehicleOptions - Editable options in edit mode
 * @param {object}  props.values                 - Formik values
 * @param {boolean} props.inEditMode             - Whether in edit mode
 */
export const VehicleOptionsRenderer = ({
  vehicleOptions,
  editableVehicleOptions,
  values,
  inEditMode,
}) => {
  const { t } = useTranslation()

  const COMPONENT_MAP = useMemo(() => ({
    CheckboxField,
    BoosterField,
  }), [])

  const processedOptions = useMemo(() => {
    return VEHICLE_OPTIONS_CONFIG.map(option =>
      processVehicleOption(option, vehicleOptions, editableVehicleOptions, values, inEditMode)
    ).filter(option => {
      if (!option.show) return false
      if (option.shouldShow && !option.shouldShow(values)) return false
      return true
    })
  }, [vehicleOptions, editableVehicleOptions, values, inEditMode])

  // Compute active-selection pill labels for the card header
  const activePills = useMemo(() => {
    const pills = []
    if (values.pet) pills.push(t('title-pet-option'))
    if (values.low) pills.push(t('title-low-option'))
    if (values.booster === 1) pills.push(`1 ${t('title-booster-option')}`)
    if (values.booster >= 2) pills.push(`${values.booster} ${t('title-booster-option')}`)
    if (values.rearFacingChildSeat) pills.push(t('title-childseat-option'))
    return pills
  }, [values.pet, values.low, values.booster, values.rearFacingChildSeat, t])

  if (processedOptions.length === 0) return null

  return (
    <AddOnsCard
      title={t('title-booking-options')}
      activePills={activePills}
    >
      {processedOptions.map((option) => {
        const FieldComponent = COMPONENT_MAP[option.componentName]

        const fieldProps = {
          name: option.formField,
          icon: option.icon,
          tabIndex: option.tabIndex,
          title: t(option.titleKey),
          component: FieldComponent,
        }

        if (option.infoKey) fieldProps.info = t(option.infoKey)
        if (inEditMode && !option.isEditable) fieldProps.disabled = true
        if (option.customProps) Object.assign(fieldProps, option.customProps(option))

        const apiKey = option.apiKeys?.[0] || option.key
        const priceAmount = getVehicleOptionPrice(values, apiKey)
        fieldProps.price = priceAmount > 0 ? `${priceAmount}:-` : t('label-option-free')

        return <Field key={option.key} {...fieldProps} />
      })}
    </AddOnsCard>
  )
}

/**
 * Hook to get processed vehicle options data without rendering.
 */
export const useVehicleOptions = ({ vehicleOptions, editableVehicleOptions, values, inEditMode }) => {
  return useMemo(() => {
    return VEHICLE_OPTIONS_CONFIG.reduce((acc, option) => {
      acc[option.key] = processVehicleOption(option, vehicleOptions, editableVehicleOptions, values, inEditMode)
      return acc
    }, {})
  }, [vehicleOptions, editableVehicleOptions, values, inEditMode])
}
